import { useApiQuery } from '../useApiQuery';
import { apiGet, operationsClient } from '@/lib/api';
import { contactKeys } from '@/lib/queryKeys';
import type { LocationContactRole } from './useLocationContacts';

/** One person attached to a location, as the contact's location list returns them. */
export interface ContactLocationPerson {
  id: string;
  contactId: string;
  role: LocationContactRole;
  isPrimary: boolean;
  title: string | null;
  receivesNotifications: boolean;
  notificationChannels: string[];
  contact: {
    id: string;
    displayName: string | null;
    firstName: string | null;
    lastName: string | null;
    companyName: string | null;
    primaryPhone: string | null;
    primaryEmail: string | null;
  };
}

/** A place this customer owns, with the people who are there. */
export interface ContactLocation {
  id: string;
  locationNumber?: string | null;
  title?: string | null;
  nickname?: string | null;
  label?: string | null;
  street1?: string | null;
  street2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  isPrimary?: boolean;
  isService?: boolean;
  isBilling?: boolean;
  isTaxExempt?: boolean;
  accessNotes?: string | null;
  status?: string | null;
  contacts?: ContactLocationPerson[];
}

/**
 * The name staff gave a place, if any: its title, nickname, or the label the
 * address form saves. Every card that names a contact's location goes through
 * this, so one place does not read "Vacation Home" on one tab and its street
 * on another. Null when it has none; callers fall back to the address.
 */
export function contactLocationName(
  location: Pick<ContactLocation, 'title' | 'nickname' | 'label'>,
): string | null {
  for (const name of [location.title, location.nickname, location.label]) {
    const trimmed = name?.trim();
    if (trimmed) return trimmed;
  }
  return null;
}

/** How many location cards a contact page draws before asking. */
export const CONTACT_LOCATIONS_PAGE_SIZE = 24;

/**
 * Every location this contact is linked to — the ones they own and the ones
 * they are named at — each with its people.
 *
 * One request rather than a list call plus a people call per card: a commercial
 * customer with thirty sites is the case this page exists for.
 *
 * Bounded on purpose. Nothing caps how many locations a customer may own since
 * the JSON-array limit was lifted, and a property manager with hundreds of
 * buildings must not turn their contact page into hundreds of cards — `total`
 * says how many there are, and the caller raises `limit` when asked to.
 */
export function useContactLocations(
  contactId: string,
  limit: number = CONTACT_LOCATIONS_PAGE_SIZE,
  search = '',
) {
  const term = search.trim();
  return useApiQuery<{ data: ContactLocation[]; total: number }>({
    queryKey: [...contactKeys.locations(contactId), limit, term],
    queryFn: () => {
      // The server matches on location number, nickname, title, label,
      // street, city and postal code — searching a paged list in the browser
      // would only ever find what the current page happens to hold.
      const params = new URLSearchParams({
        // Both sides. A customer OWNS places and a site contact is NAMED at
        // them, and the same person can be both — asking for one side made a
        // site contact's places vanish the moment they were given one of
        // their own.
        relatedContactId: contactId,
        includeContacts: 'true',
        limit: String(limit),
      });
      if (term) params.set('search', term);
      return apiGet<{ data: ContactLocation[]; total: number }>(
        operationsClient,
        `/locations?${params.toString()}`,
      );
    },
    enabled: Boolean(contactId),
    placeholderData: (previous) => previous,
  });
}
